import React, { useCallback, useEffect, useState } from "react";
import { Alert } from "../../../../../domain/alert/Alert";
import { CareRecipient } from "../../../../../domain/care_recipient/CareRecipient";
import { CareRecipientId } from "../../../../../domain/care_recipient/CareRecipientId";
import { CareRecipientWithDataSyncInformation } from "../../../../../domain/care_recipient/CareRecipientWithDataSyncInformation";
import { DateObject } from "../../../../../domain/fundamental/DateObject";
import { Paths } from "../../../../../Paths";
import { useAppDispatch } from "../../../../../state/store";
import { checkDisableAlertAction, disableAlertAction, toggleAlertDisplayAction, updatePeriodAction } from "../../../../../state/usecase/admin/care_recipient/actions";
import { selectCareRecipientAction } from "../../../../../state/usecase/care_recipient/actions";
import useAlerts from "../../../../hooks/admin/care_recipient/useAlerts";
import { SwitchButton } from "../../../common/button/SwitchButton";
import { InputCheckBox } from "../../../common/input/InputCheckBox";
import { DateSelector } from "./DateSelector";
import { TableMenuButton } from "./TableMenuButton";
import { GraphIcon } from "../../../common/icons/GraphIcon";
import { HukidashiIcon } from "../../../common/icons/HukidashiIcon";
import NotificationIcon from "../../../common/icons/NotificationIcon";
import { useNavigate } from "react-router-dom";

export const AlertsTableBody = React.memo(function AlertsTableBody(props: AlertsTableBodyProps) {
    const dispatch = useAppDispatch();

    const navigate = useNavigate();

    const { checkedAlertIds } = useAlerts();

    const { alerts } = props;

    const handleAlertCheck = useCallback((alertId: string) => () => {
        dispatch(
            checkDisableAlertAction({ alertId: alertId })
        );
    }, [dispatch]);

    const handleClickDashboard = useCallback((careRecipient: CareRecipient) => () => {
        dispatch(selectCareRecipientAction({ id: careRecipient.id, name: careRecipient.name, houseId: careRecipient.houseId, branchCode: careRecipient.branchCode, customerCode: careRecipient.customerCode }));
        navigate(`${Paths.dashBoard}/${careRecipient.id.value}`);
    }, [dispatch, navigate]);

    const handleClickDaily = useCallback((careRecipient: CareRecipient, date: string) => () => {
        dispatch(selectCareRecipientAction({ id: careRecipient.id, name: careRecipient.name, houseId: careRecipient.houseId, customerCode: careRecipient.customerCode, branchCode: careRecipient.branchCode }));
        navigate(`${Paths.daily}/${careRecipient.id.value}/${date}`);
    }, [dispatch, navigate]);

    const handleClickAlertDisabled = useCallback((alertMessageId: string) => () => {
        if (window.confirm("アラートを解除しますか？")) {
            dispatch(
                disableAlertAction({ alertId: alertMessageId })
            );
        }
    }, [dispatch]);

    const handlePeriod = useCallback((careRecipientId: CareRecipientId, startDate: DateObject, endDate: DateObject) => {
        dispatch(updatePeriodAction(
            { careRecipientId: careRecipientId.value, startDate: startDate.format(), endDate: endDate.format() }
        ));
    }, [dispatch]);

    const handleClickCareCommentRegister = useCallback((careRecipientId: CareRecipientId, alert: Alert) => () => {
        navigate(`${Paths.admin}${Paths.careComment}${Paths.add}/${careRecipientId.value}?alert_id=${alert.id}`);
    }, [navigate]);

    return (
        <tbody>
            {alerts?.map((alert, _) => (
                <TableRow
                    key={alert.alert.id}
                    alert={alert.alert}
                    checked={checkedAlertIds.includes(alert.alert.id)}
                    handleAlertCheck={handleAlertCheck}
                    careRecipientWithDataSyncInfo={alert.careRecipient}
                    handlePeriod={handlePeriod}
                    startDate={alert.period?.start ?? null}
                    endDate={alert.period?.end ?? null}
                    handleClickDashboard={handleClickDashboard}
                    handleClickDaily={handleClickDaily}
                    handleClickAlertDisabled={handleClickAlertDisabled}
                    handleClickCareCommentRegister={handleClickCareCommentRegister}
                />
            ))}
        </tbody>
    );
});

const TableRow = React.memo(function TableRow(props: TableRowProps) {
    const {
        alert,
        checked,
        handleAlertCheck,
        careRecipientWithDataSyncInfo,
        handlePeriod,
        startDate,
        endDate,
        handleClickDashboard,
        handleClickDaily,
        handleClickAlertDisabled,
        handleClickCareCommentRegister
    } = props;

    const dispatch = useAppDispatch();

    const { careRecipient } = careRecipientWithDataSyncInfo;

    const [alertPeriodStart, setAlertPeriodStart] = useState(startDate);

    const [alertPeriodEnd, setAlertPeriodEnd] = useState(endDate);

    const setCalendarDate = useCallback((startDate: DateObject, endDate: DateObject) => {
        handlePeriod(careRecipient.id, startDate, endDate);
    }, [careRecipient, handlePeriod]);

    useEffect(() => {
        // アラート一覧の別アラート・利用者一覧から更新される場合があるので、startDate, endDateの変更を検知したらsetStateする
        setAlertPeriodStart(startDate);
        setAlertPeriodEnd(endDate);
    }, [startDate, endDate]);

    const handleSwitch = useCallback(() => {
        dispatch(
            toggleAlertDisplayAction({
                alertId: alert.id
            })
        );
    }, [alert, dispatch]);

    return (
        <tr>
            <td>
                <InputCheckBox label={""} checked={checked} handleCheckBoxChanged={handleAlertCheck(alert.id)} />
            </td>
            <td>{careRecipient?.houseId?.value}</td>
            <td>{careRecipient.name.value}</td>
            <td>
                <div>{careRecipientWithDataSyncInfo.dataSyncStartTime?.format() ?? "-"}</div>
                <div className="date_selector">
                    <DateSelector
                        defaultStartDate={alertPeriodStart}
                        defaultEndDate={alertPeriodEnd}
                        handleSaveClicked={setCalendarDate}
                        dataSyncStartDate={careRecipientWithDataSyncInfo.dataSyncStartTime?.date}
                    />
                </div>
                <div>{careRecipientWithDataSyncInfo.dataSyncEndTime?.format() ?? "-"}</div>
            </td>
            <td>
                <div className="alert">
                    <div className={`alert__label__${alert.createdFrom.toLowerCase()}`}>{alert.getCreaterLabel()}</div>
                    <div className="alert__message">
                        <div className="date">{alert.date}</div>
                        <div className="message">{alert.message}</div>
                    </div>
                </div>
            </td>
            <td>
                <div className="menu_buttons">
                    <TableMenuButton
                        onClick={handleClickDashboard(careRecipient)}
                        icon={<GraphIcon />}
                        label={"ダッシュボード"}
                    />
                    <TableMenuButton
                        onClick={handleClickDaily(careRecipient, alert.date)}
                        icon={<GraphIcon />}
                        label={"デイリー"}
                    />
                    <TableMenuButton
                        onClick={handleClickCareCommentRegister(careRecipient.id, alert)}
                        icon={<HukidashiIcon />}
                        label={"アドバイスコメント"}
                    />
                    <TableMenuButton
                        className={"remove"}
                        onClick={handleClickAlertDisabled(alert.id)}
                        icon={<NotificationIcon />}
                        label={"解除"}
                    />
                </div>
            </td>
            <td>
                <SwitchButton onClick={handleSwitch} switchOn={alert.display} />
            </td>
        </tr>
    );
});

type AlertsTableBodyProps = {
    alerts: {
        careRecipient: CareRecipientWithDataSyncInformation,
        period: { start: DateObject | null, end: DateObject | null } | null,
        alert: Alert
    }[]
}

type TableRowProps = {
    alert: Alert
    checked: boolean
    handleAlertCheck: (alertId: string) => () => void
    careRecipientWithDataSyncInfo: CareRecipientWithDataSyncInformation
    handlePeriod: (careRecipientId: CareRecipientId, startDate: DateObject, endDate: DateObject) => void
    startDate: DateObject | null
    endDate: DateObject | null
    handleClickDashboard: (careRecipient: CareRecipient) => () => void
    handleClickDaily: (careRecipient: CareRecipient, date: string) => () => void
    handleClickAlertDisabled: (alertMessageId: string) => () => void
    handleClickCareCommentRegister: (careRecipientId: CareRecipientId, alert: Alert) => () => void
}
